import React, { useEffect, useState } from "react";
import { Alert, Group, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { notifySuccess } from "../../utils/notify";
import {
  CHECKOUT_REMINDER_EMOJIS,
  CHECKOUT_REMINDER_TEXT_MAX,
  CHECKOUT_REMINDER_TITLE_MAX,
  DEFAULT_CHECKOUT_REMINDER_TITLE,
} from "../../utils/checkoutReminder";
import { BusinessInfo, SalonContextType } from "../../types/context";
import { CheckoutReminderAlert } from "../common/CheckoutReminderAlert";
import { EmojiPicker } from "../common/EmojiPicker";
import styles from "./CheckoutReminderSettings.module.css";
import {
  SettingsPageLayout,
  SettingsPageHeader,
  SettingsSection,
  SettingsField,
  SettingsTabShell,
  SettingsEditButton,
} from "./common";

const SUBTITLE =
  "Show staff a note below the Total in the appointment summary and on the checkout screen.";

const PREVIEW_PLACEHOLDER_TEXT = "Your reminder message will appear here.";

/** The salon's checkout reminder as the form holds it. */
interface CheckoutReminderFormValues {
  title: string;
  text: string;
  emoji: string | null;
}

/** A null title starts as "Before checkout" — a real value, not a placeholder. */
const toFormValues = (
  info: Partial<BusinessInfo> | null | undefined,
): CheckoutReminderFormValues => ({
  title:
    info?.checkout_reminder_title?.trim() || DEFAULT_CHECKOUT_REMINDER_TITLE,
  text: info?.checkout_reminder_text ?? "",
  emoji: info?.checkout_reminder_emoji?.trim() || null,
});

/** Emoji (or the default icon) shown beside the title in the read view. */
const ReminderEmoji = ({ emoji }: { emoji: string | null }) =>
  emoji ? (
    <Text component="span" aria-hidden>
      {emoji}
    </Text>
  ) : (
    <IconInfoCircle size={16} aria-hidden />
  );

interface CheckoutReminderSettingsProps {
  /** Hide the Edit affordance (no permission to edit the salon record). */
  readOnly?: boolean;
}

/**
 * Settings → Checkout → Checkout Reminders. Configures the salon-level reminder
 * (emoji + title in one input, message) that `ConnectedCheckoutReminderAlert`
 * renders below the Total in TransactionSummary and CheckoutPanel. Always on:
 * it shows whenever the message is non-blank, so clearing the message removes
 * it. The fields live on the salon record, so this reads
 * SalonContext.businessInfo (no fetch) and saves via PUT /business-locations — the
 * Off-Platform Payments pattern.
 */
const CheckoutReminderSettings: React.FC<CheckoutReminderSettingsProps> = ({
  readOnly = false,
}) => {
  const { businessInfo, setBusinessInfo } = useSalon() as SalonContextType;
  const { updateSalon } = useApp();
  const salonId = businessInfo?.id ?? "";

  // `saved` backs the read values; `draft` backs the edit controls and is
  // re-seeded from `saved` on each Edit.
  const [saved, setSaved] = useState<CheckoutReminderFormValues>(() =>
    toFormValues(businessInfo),
  );
  const [draft, setDraft] = useState<CheckoutReminderFormValues>(saved);
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // businessInfo can arrive (or refresh) after mount; resync while not editing.
  useEffect(() => {
    if (!editing) setSaved(toFormValues(businessInfo));
  }, [businessInfo, editing]);

  const setField = <K extends keyof CheckoutReminderFormValues>(
    field: K,
    value: CheckoutReminderFormValues[K],
  ) => setDraft((prev) => ({ ...prev, [field]: value }));

  const startEdit = () => {
    setDraft(saved);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!salonId) return;
    setIsSaving(true);
    setError(null);
    const fields = {
      checkout_reminder_title: draft.title.trim() || null,
      checkout_reminder_text: draft.text.trim() || null,
      checkout_reminder_emoji: draft.emoji,
    };
    try {
      // Send ONLY the reminder fields: PUT /business-locations writes every recognized key
      // it receives, so spreading businessInfo would replay stale values.
      await updateSalon({ id: salonId, ...fields });
      // updateSalon doesn't refetch — merge into SalonContext so the checkout
      // surfaces pick the change up immediately. Functional update so a
      // context refresh during the request isn't overwritten by a stale copy.
      setBusinessInfo((prev) => (prev ? { ...prev, ...fields } : prev));
      setSaved(toFormValues(fields));
      setEditing(false);
      notifySuccess(
        fields.checkout_reminder_text
          ? "Staff will see your reminder before checkout."
          : "The message is empty, so staff won't see a reminder.",
        { title: "Checkout reminder saved" },
      );
    } catch (err) {
      console.error("Error saving checkout reminder:", err);
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Failed to save the checkout reminder. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Saving needs the salon id, so don't offer Edit until businessInfo loads.
  const canEdit = !readOnly && Boolean(salonId);

  const header = (
    <SettingsPageHeader
      title="Checkout Reminders"
      subtitle={SUBTITLE}
      actions={
        canEdit && !editing ? (
          <SettingsEditButton onClick={startEdit} />
        ) : undefined
      }
    />
  );

  const shown = editing ? draft : saved;
  const hasMessage = shown.text.trim().length > 0;

  return (
    <SettingsPageLayout header={header}>
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <SettingsTabShell
        editing={editing}
        saving={isSaving}
        readOnly={!canEdit}
        hideEditButton
        onEdit={startEdit}
        onCancel={cancelEdit}
        onSave={handleSave}
      >
        <Stack gap="md">
          <SettingsSection
            title="Reminder"
            description="Staff read this before they proceed to checkout."
          >
            <SettingsField
              label="Title"
              info="Pick an emoji on the left, then name the reminder."
              editing={editing}
              editControl={
                // Emoji picker inside the title input, like appointment tags
                // (AppointmentTagFormModal).
                <TextInput
                  aria-label="Title"
                  value={draft.title}
                  onChange={(event) =>
                    setField("title", event.currentTarget.value)
                  }
                  maxLength={CHECKOUT_REMINDER_TITLE_MAX}
                  leftSection={
                    <EmojiPicker
                      value={draft.emoji}
                      onChange={(emoji) => setField("emoji", emoji)}
                      options={CHECKOUT_REMINDER_EMOJIS}
                      defaultIcon={<IconInfoCircle size={15} stroke={2} />}
                      ariaLabel="Choose reminder emoji"
                      title="Choose an emoji"
                      disabled={isSaving}
                    />
                  }
                  leftSectionWidth={40}
                  leftSectionPointerEvents="all"
                  w="100%"
                />
              }
            >
              {/* Dimmed while there's no message: nothing shows at checkout. */}
              <Group
                gap={6}
                wrap="nowrap"
                c={saved.text.trim() ? undefined : "dimmed"}
              >
                <ReminderEmoji emoji={saved.emoji} />
                {saved.title}
              </Group>
            </SettingsField>

            <SettingsField
              label="Message"
              info="Leave it empty to hide the reminder."
              editing={editing}
              withDivider={false}
              editControl={
                <Textarea
                  aria-label="Message"
                  value={draft.text}
                  onChange={(event) =>
                    setField("text", event.currentTarget.value)
                  }
                  placeholder="e.g. Take a photo of the finished service for the client's profile."
                  maxLength={CHECKOUT_REMINDER_TEXT_MAX}
                  autosize
                  minRows={3}
                  maxRows={8}
                  description={`${draft.text.length} / ${CHECKOUT_REMINDER_TEXT_MAX}`}
                  inputWrapperOrder={["input", "description", "error"]}
                  w="100%"
                />
              }
            >
              {saved.text.trim() ? (
                <Text inherit className={styles.preWrap}>
                  {saved.text}
                </Text>
              ) : (
                <Text inherit c="dimmed">
                  Not set — no reminder shows at checkout
                </Text>
              )}
            </SettingsField>
          </SettingsSection>

          <SettingsSection
            title="Preview"
            description="How staff will see it below the Total."
          >
            <Stack gap="xs">
              <CheckoutReminderAlert
                title={shown.title.trim() || DEFAULT_CHECKOUT_REMINDER_TITLE}
                text={hasMessage ? shown.text.trim() : PREVIEW_PLACEHOLDER_TEXT}
                emoji={shown.emoji}
              />
              {!hasMessage && (
                <Text size="sm" c="dimmed">
                  Add a message to show the reminder at checkout.
                </Text>
              )}
            </Stack>
          </SettingsSection>
        </Stack>
      </SettingsTabShell>
    </SettingsPageLayout>
  );
};

export default CheckoutReminderSettings;
